import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { useModalProvider } from "../providers/ModalProvider";
import { useUserProvider } from "../providers/UserProvider";
import { useLoaderProvider } from "../providers/LoaderProvider";
import { GetEvent, UpdateEvent } from "../services/api/events";
import { AddAppResource } from "../services/api/app";
import Page from "../components/Page";
import {
  Column,
  EntryForm,
  Input,
  Select,
  TextArea,
} from "../components/EntryForm";
import DateSelector from "../components/DateSelector";
import MediaUploader from "../components/MediaUploader";
import responseProcessor from "../lib/responseProcessor";
import { validations } from "../lib/form";
import DataSelector from "../components/DataSelector";
import { Radio } from "antd";
import { IsLowerVersion } from "../lib/useVersionizer";

const EventCalendarDetail = ({ newEvent, match }) => {
  const [initialValues, setInitialValues] = useState({
    category: "",
    name: "",
    description: "",
    video_url: "",
    image_url: "",
    month: "",
    day_of_month: 1,
    time: "",
    duration: "",
    start_date: "",
    capacity: "",
    location: "",
    meeting_url: "",
    expiration: moment().utcOffset(0, true),
    action_bar_text: "",
    action_bar_url: "",
    media_metadata: {},
    tags: [],
    cta_type: "URL CTA",
    cta: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [metadata, setMetadata] = useState({});
  const { user } = useUserProvider();
  const { showModal } = useModalProvider();
  const { doSetShowLoadingModal } = useLoaderProvider();
  const uuid = newEvent ? user.currentApp() : match.params.uuid;
  const history = useHistory();
  const shouldHideMonthDayFields = !IsLowerVersion('10.0');

    const showBusiness = useMemo(() => (user?.app_data?.setup?.enterprise && user?.app_data?.setup?.enterprise_launch_business), [user]);

  useEffect(() => {
    if (newEvent) {
      setInitialValues({
        category: "",
        name: "",
        description: "",
        video_url: "",
        image_url: "",
        month: "",
        day_of_month: 1,
        time: "",
        duration: "",
        start_date: "",
        capacity: "",
        location: "",
        meeting_url: "",
        expiration: "",
        // expiration: moment().utcOffset(0, true),
        action_bar_text: "",
        action_bar_url: "",
        tags: [],
        cta_type: "URL CTA",
        cta: null,
      });
    } else {
      setIsLoading(true);
      GetEvent(uuid, user.idToken)
        .then((res) => res.json())
        .then((res) => {
          setInitialValues({
            category: res.data.category,
            name: res.data.name,
            description: res.data.description,
            video_url: res.data.video_url,
            image_url: res.data.image_url,
            month: res.data.month,
            day_of_month: res.data.day_of_month,
            time: res.data.time,
            duration: res.data.duration || "",
            start_date: res.data.start_date || "",
            capacity: res.data.capacity || "",
            location: res.data.location,
            meeting_url: res.data.meeting_url || "",
            expiration: res.data.expiration,
            cta_type: res?.data?.cta_type,
            cta: res?.data?.cta,
            action_bar_text: res.data.action_bar_text,
            action_bar_url: res.data.action_bar_url,
            tags: res.data.tags,
          });
          setMetadata(res.data.media_metadata);
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    doSetShowLoadingModal(isLoading);
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentAction = newEvent ? "New" : "Edit";
  const currentUrl = newEvent ? "/events/new" : `/events/${uuid}`;

  let links = [
    { name: "Home", url: "/" },
    { name: "Event Calendar", url: "/events" },
    { name: `${currentAction} Event`, url: currentUrl },
  ];

  let schema = {
    category: Yup.string().oneOf(["Live", "Online"]).required("Required"),
    name: validations.str_req_255,
    description: validations.str_multiline,
    video_url: validations.long_text,
    image_url: validations.long_text,
    month: Yup.string().nullable(),
    day_of_month: Yup.number()
      .typeError("Must be a number from 1 to 31")
      .integer()
      .min(1, "Minimum day of month: 1")
      .max(31, "Maximum day of month: 31")
      .required("Required"),
    time: validations.str_req_20,
    duration: validations.str_req_30,
    start_date: Yup.date().required("Required"),
    capacity: validations.str_req_30,
    location: validations.str_req_20,
    meeting_url: validations.long_text,
    expiration: Yup.date().required("Required"),
    action_bar_text: validations.str_notreq_50,
    action_bar_url: validations.str_255,
    cta_type: Yup.string().required("Required"),
    cta: Yup.object({
      id: Yup.string().nullable(),
      name: Yup.string().nullable(),
    }).nullable(),
  };

  const responseProcessorProxy = (response) => {
    responseProcessor({
      response,
      showModal,
      onSuccess: () => {
        setIsLoading(false)
        history.push("/events")
      }
    });
  };

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object().shape(schema),
    onSubmit: async (values) => {
      setIsLoading(true)
      if (newEvent) {
        let payload = values

        if (["URL CTA", "Launch Business"].includes(payload?.cta_type)) {
          payload.cta = {}
        }

        AddAppResource(uuid, "events", user.idToken, { data: values }).then(
          (response) => responseProcessorProxy(response)
        );
      } else {
        UpdateEvent(uuid, user.idToken, { data: values }).then((response) =>
          responseProcessorProxy(response)
        );
      }
    },
  });

  return (
    <Page links={links} title={`${currentAction} Event`}>
      <EntryForm
        isLoading={isLoading}
        formik={formik}
        submitHandler={formik.handleSubmit}
        cancelURL="/events"
      >
        <Column>
          <Select name="category" label="Type" formik={formik}>
            <option value={null} label="Select Type..." />
            <option value="Live" label="Live" />
            <option value="Online" label="Online" />
          </Select>
          <Input name="name" type="text" label="Event Name" formik={formik} />
          <TextArea name="description" label="Description" formik={formik} />
          <MediaUploader
            name="video_url"
            type="text"
            label="Video URL"
            formik={formik}
            metadata={metadata}
            mediaType="video"
          />
          <MediaUploader
            name="image_url"
            type="text"
            label="Image URL"
            formik={formik}
            metadata={metadata}
            mediaType="image"
          />
          <Input name="meeting_url" type="url" label="Meeting URL" formik={formik} />
          <DataSelector
            name="tags"
            formik={formik}
            label="Tags"
            resourceEndpoint="tags?category=Content"
            filterOptions={(values) => values.filter((a) => !a.is_expired)}
          />
          {!shouldHideMonthDayFields && (
            <Select name="month" label="Month" formik={formik}>
              <option value={null} label="Select Month..." />
              <option value="JAN" label="JAN" />
              <option value="FEB" label="FEB" />
              <option value="MAR" label="MAR" />
              <option value="APR" label="APR" />
              <option value="MAY" label="MAY" />
              <option value="JUN" label="JUN" />
              <option value="JUL" label="JUL" />
              <option value="AUG" label="AUG" />
              <option value="SEP" label="SEP" />
              <option value="OCT" label="OCT" />
              <option value="NOV" label="NOV" />
              <option value="DEC" label="DEC" />
            </Select>
          )}
        </Column>
        <Column>
          {!shouldHideMonthDayFields && (
            <Input
              name="day_of_month"
              type="number"
              min={1}
              max={31}
              label="Day of Month"
              formik={formik}
            />
          )}
          <Input name="time" type="text" label="Time" formik={formik} />
          <Input name="duration" type="text" label="Duration" formik={formik} />
          <DateSelector
            formik={formik}
            name="start_date"
            label="Start Date"
            onChange={(selectedDate) =>
              formik.setFieldValue("start_date", selectedDate || "")
            }
          />
          <Input name="capacity" type="text" label="Capacity" formik={formik} />
          <Input name="location" type="text" label="Location" formik={formik} />
          <DateSelector
            formik={formik}
            name="expiration"
            label="Event Listing Expires (Date)"
            onChange={(selectedDate) =>
              formik.setFieldValue("expiration", selectedDate || "")
            }
          />
          <div className="flex flex-row w-full justify-between pb-4">
            <label
              className={`text-lg mb-5`}
              htmlFor={"cta_type"}
            >
              CTA Type
              <span style={{ color: "red" }}> *</span>
            </label>
            <Radio.Group
              formik={formik}
              className="flex flex-row"
              value={formik?.values?.cta_type ?? "URL CTA"}
              onChange={event => {
                formik.setFieldValue("cta_type", event.target.value)
                formik.setFieldValue("cta", null)
                formik.setFieldValue("action_bar_text", "")
                formik.setFieldValue("action_bar_url", "")
              }}
            >
              <Radio
                value="URL CTA"
              >
                URL CTA
              </Radio>
              <Radio
                value="Preset CTA"
              >
                Preset CTA
              </Radio>
            </Radio.Group>
          </div>
          {
            !["Preset CTA"].includes(formik.values.cta_type) ? (
              <Input
                name="action_bar_text"
                type="text"
                label="Call to Action Text"
                formik={formik}
              />
            ) : null
          }

          {
            !["Preset CTA"].includes(formik.values.cta_type) ? (
              <Input
                name="action_bar_url"
                type="text"
                label="Call to Action URL"
                formik={formik}
              />
            ) : null
          }

          {
            formik.values.cta_type === "Preset CTA" ? (
              <DataSelector
                name="cta"
                formik={formik}
                label="Call to Action Name List"
                resourceEndpoint="call_to_actions"
                single
                singleSelectOnly
                withSearch
              />
            ) : null
          }
        </Column>
      </EntryForm>
    </Page>
  );
};

export default EventCalendarDetail;
